import { useState, useRef, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import "../../assets/styles/login.css";
import { LuArrowRight, LuCheck, LuChevronDown, LuCircleX, LuClock, LuEye, LuEyeOff, LuLock, LuMail, LuUser } from "react-icons/lu";

// "kavin.s@company.com" → "kav***@company.com"
function maskEmail(email) {
  const e = String(email || "").trim();
  const at = e.indexOf("@");
  if (at < 0) return e;
  const local = e.slice(0, at);
  const domain = e.slice(at);
  const visible = local.slice(0, Math.min(3, Math.floor(local.length / 2)));
  return `${visible}${"*".repeat(Math.max(3, local.length - visible.length))}${domain}`;
}

const ROLE_OPTIONS = [
  { value: "superadmin", label: "Super Admin" },
  { value: "hr", label: "HR" },
  { value: "pm", label: "Project Manager" },
  { value: "itadmin", label: "IT Admin" },
  { value: "employee", label: "Employee" },
  { value: "intern", label: "Intern" },
];

// Public OAuth client ID (safe to ship in the frontend). The client SECRET stays
// server-side in backend/.env and is never exposed here.
const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  "1017932918600-tv7n7814qbu31uk84fqf5uismqq0g3qr.apps.googleusercontent.com";

// Google's multi-colour "G" mark.
const GoogleG = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

// `onBack` comes from the landing page at "/" — it is absent when /login is
// reached directly, and the "Explore PSHRMS" link is hidden in that case.
function LoginPage({ onBack }) {
  const { login, verifyTwoFactor, loginWithGoogle } = useAuth();
  const [loginId, setLoginId] = useState("");
  const [pw, setPw] = useState("");
  const [role, setRole] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const roleRef = useRef(null);
  // ── Two-step verification (password login only) ──
  const [twoFaChallenge, setTwoFaChallenge] = useState("");
  const [twoFaCode, setTwoFaCode] = useState("");
  // ── Sign in with Google (GIS auth-code popup) ──
  const codeClientRef = useRef(null);
  const roleValRef = useRef(role);   // latest role for the async Google callback
  useEffect(() => { roleValRef.current = role; }, [role]);

  /* This page offers NO LoginHub button by design. People reach HRMS by
     clicking it in the portal, which sends them straight to /auth/login — the
     form below is the password login, unchanged, and it stays the way in when
     the portal is unavailable.

     The one thing that does reach this page from the portal is a REFUSAL: a
     sign-in that could not be completed comes back as /login?sso_error=<code>.
     Without this the person would land on a blank login form with no idea why,
     which is the same screen as "your password was wrong". Nothing renders
     unless that parameter is present, so a normal visit is untouched. */
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("sso_error");
    if (!code) return;
    const MESSAGES = {
      not_in_hrms: "That account is not on record in HRMS. Please contact HR — signing in cannot create an HRMS account.",
      already_linked: "This HRMS record is already linked to a different LoginHub account. Please contact HR.",
      deactivated: "Your account has been deactivated. Please contact HR.",
      expired: "That sign-in attempt expired. Please try again.",
      portal_error: "The sign-in portal reported a problem. Please try again.",
    };
    setErr(MESSAGES[code] || "Sign-in could not be verified. Please try again, or use your HRMS password below.");
    window.history.replaceState({}, "", "/login");
  }, []);

  // ── Forgot password state machine ──
  // 'login' | 'forgot-email' | 'forgot-otp' | 'forgot-success'
  const [authView, setAuthView] = useState("login");
  const [fpEmail, setFpEmail] = useState("");
  const [fpOtp, setFpOtp] = useState("");
  const [fpNewPw, setFpNewPw] = useState("");
  const [fpConfirmPw, setFpConfirmPw] = useState("");
  const [fpInfo, setFpInfo] = useState("");
  const [fpErr, setFpErr] = useState("");
  const [fpBusy, setFpBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);   // seconds remaining
  const [showNewPw, setShowNewPw] = useState(false);

  // Countdown for "Resend OTP" button
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const goLogin = () => {
    setAuthView("login");
    setFpEmail(""); setFpOtp(""); setFpNewPw(""); setFpConfirmPw("");
    setFpInfo(""); setFpErr(""); setFpBusy(false); setResendIn(0);
  };

  const submitForgotEmail = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setFpErr(""); setFpInfo("");
    if (!fpEmail.trim()) { setFpErr("Please enter your email."); return; }
    setFpBusy(true);
    try {
      await api.forgotPassword(fpEmail.trim());
      setAuthView("forgot-otp");
      setResendIn(60);
      setFpInfo(`If an account exists for ${fpEmail.trim()}, a 6-digit code has been sent.`);
    } catch (err) {
      setFpErr(err.message || "Could not send OTP. Try again.");
    } finally { setFpBusy(false); }
  };

  const submitResetPassword = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setFpErr(""); setFpInfo("");
    if (!/^\d{6}$/.test(fpOtp.trim())) { setFpErr("Enter the 6-digit OTP from your email."); return; }
    if (fpNewPw.length < 8 || !/[A-Z]/.test(fpNewPw) || !/\d/.test(fpNewPw)) {
      setFpErr("Password must be at least 8 characters with one uppercase letter and one number.");
      return;
    }
    if (fpNewPw !== fpConfirmPw) { setFpErr("Passwords do not match."); return; }
    setFpBusy(true);
    try {
      await api.resetPassword(fpEmail.trim(), fpOtp.trim(), fpNewPw);
      setAuthView("forgot-success");
    } catch (err) {
      setFpErr(err.message || "Invalid or expired OTP.");
    } finally { setFpBusy(false); }
  };

  const resendOtp = async () => {
    if (resendIn > 0 || fpBusy) return;
    setFpBusy(true);
    setFpErr("");
    try {
      await api.forgotPassword(fpEmail.trim());
      setResendIn(60);
      setFpInfo("A new code has been sent to your email.");
    } catch (err) {
      setFpErr(err.message || "Could not resend OTP.");
    } finally { setFpBusy(false); }
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (roleRef.current && !roleRef.current.contains(e.target)) setRoleOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Load Google Identity Services and build the auth-code client. Uses polling
  // rather than a single onload handler so it survives script-load timing and
  // React StrictMode's double mount (either of which can drop a one-shot onload).
  useEffect(() => {
    let cancelled = false;
    const tryInit = () => {
      if (cancelled || codeClientRef.current) return true;
      if (!window.google?.accounts?.oauth2) return false;
      try {
        codeClientRef.current = window.google.accounts.oauth2.initCodeClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: "openid email profile",
          ux_mode: "popup",
          callback: async (resp) => {
            if (resp?.error) { setErr("Google sign-in was cancelled."); return; }
            if (!resp?.code) return;
            setErr(""); setLoading(true);
            try {
              const res = await loginWithGoogle(resp.code, roleValRef.current);
              // 2FA on → show the 6-digit code step instead of finishing login.
              if (res && res.twoFactorRequired) {
                setTwoFaChallenge(res.challengeToken);
                setTwoFaCode("");
                setAuthView("2fa");
                setLoading(false);
              }
              // else success → AuthContext sets the user; the app router takes over.
            } catch (e) {
              setErr(e.message || "Google sign-in failed.");
              setLoading(false);
            }
          },
        });
        return true;
      } catch (_) { return false; }
    };

    if (tryInit()) return undefined;

    if (!document.getElementById("gsi-client")) {
      const s = document.createElement("script");
      s.id = "gsi-client";
      s.src = "https://accounts.google.com/gsi/client";
      s.async = true; s.defer = true;
      document.body.appendChild(s);
    }
    const iv = setInterval(() => { if (tryInit()) clearInterval(iv); }, 300);
    const to = setTimeout(() => clearInterval(iv), 15000);
    return () => { cancelled = true; clearInterval(iv); clearTimeout(to); };
  }, [loginWithGoogle]);

  const handleGoogle = () => {
    if (!role) { setErr("Please select your role first, then continue with Google."); return; }
    if (!codeClientRef.current) { setErr("Google sign-in is still loading — please try again in a moment."); return; }
    setErr("");
    codeClientRef.current.requestCode();
  };

  const selectedRole = ROLE_OPTIONS.find((r) => r.value === role) || null;

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!role) {
      setErr("Please select your role from the Login As dropdown.");
      return;
    }
    if (!loginId.trim() || !pw.trim()) {
      setErr("Please enter Login ID/Email and Password.");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const res = await login(loginId.trim(), pw.trim(), role);
      // 2FA on → show the 6-digit code step instead of finishing login.
      if (res && res.twoFactorRequired) {
        setTwoFaChallenge(res.challengeToken);
        setTwoFaCode("");
        setAuthView("2fa");
        setLoading(false);
      }
      // else success → AuthContext sets the user; the app router takes over.
    } catch (error) {
      setErr(error.message || "Invalid email or password.");
      setLoading(false);
    }
  };

  const submitTwoFactor = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const code = twoFaCode.trim();
    if (!/^\d{6}$/.test(code)) { setErr("Enter the 6-digit code from your authenticator app."); return; }
    setLoading(true);
    setErr("");
    try {
      await verifyTwoFactor(twoFaChallenge, code, pw.trim());
      // success → AuthContext sets the user; the app router takes over.
    } catch (error) {
      // An expired challenge can never succeed — send them back to the password
      // form instead of leaving them retrying a dead step.
      if (error.code === "CHALLENGE_EXPIRED") {
        setAuthView("login");
        setTwoFaChallenge(""); setTwoFaCode("");
        setErr("That took too long — please sign in again.");
        setLoading(false);
        return;
      }
      setErr(error.message || "Invalid or expired code.");
      setLoading(false);
    }
  };

  const cancelTwoFactor = () => {
    setAuthView("login");
    setTwoFaChallenge(""); setTwoFaCode(""); setErr(""); setLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-left">
        {/* No drifting bubbles on this side — the form panel has them, this
            one does not. It already carries the pulsing glow, the blueprint
            grid and the turning helm, and a fourth moving layer behind the
            wordmark was one too many. */}
        <div className="login-left-glow" />
        <div className="login-left-dots" />
        <div className="login-left-content">
          {/* The helm turns around the logo, not around the panel: the wrapper
              shrink-wraps the logo box, and the wheel is centred on THAT. So
              the company mark sits exactly at the hub however the panel is
              sized — no magic offset to re-tune when the type above or below
              it changes. It comes first in the DOM so the box paints over it.

              Plain <img> with a CSS rotation, not the framer component from
              LandingVisuals: LoginPage is imported eagerly, and pulling that in
              would drag the chart code into the main bundle for everyone. */}
          <div className="login-mark">
            <img src="/helm.png" alt="" className="login-left-helm" aria-hidden="true" draggable="false" />
            <div className="login-logo-box">
              <img
                src="/logo.png"
                alt="Ponsuvai Foods Private Limited"
                className="login-logo-img"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <div className="login-logo-fallback" style={{ display: "none" }}>
                <LuClock size={48} color="var(--brand-800)" strokeWidth={1.5} />
              </div>
            </div>
          </div>
          <h1 className="login-brand">PSHRMS</h1>
          <p className="login-brand-sub">Human Resource Management System</p>
          <p className="login-brand-co">Ponsuvai Foods Private Limited</p>
        </div>
      </div>

      <div className="login-right">
        {/* Drifting bubbles behind the form. Six, all different sizes and all
            on different durations, so they never fall into a visible pattern —
            the sizes and paths live in CSS (.login-bub-1…6), this only says how
            many there are. Decorative and inert: aria-hidden, no pointer
            events, and stopped entirely under prefers-reduced-motion. */}
        <div className="login-bubbles" aria-hidden="true">
          {Array.from({ length: 6 }, (_, i) => (
            <span key={i} className={`login-bub login-bub-${i + 1}`} />
          ))}
        </div>
        <div className="login-form-wrap">
          <h2 className="login-title">Welcome to Ponsuvai Foods Private Limited</h2>

          {err && (authView === "login" || authView === "2fa") && (
            <div className="login-error">
              <LuCircleX size={16} color="var(--danger-500)" />
              {err}
            </div>
          )}

          {authView === "login" && (
          <form onSubmit={handleSubmit}>
            <div className="login-field" ref={roleRef}>
              <label className="login-label">
                LOGIN AS <span className="login-required-star">*</span>
              </label>
              <div className={`login-role-select ${roleOpen ? "is-open" : ""} ${selectedRole ? "has-value" : ""}`}>
                <button
                  type="button"
                  className="login-role-trigger"
                  onClick={() => setRoleOpen((v) => !v)}
                  aria-haspopup="listbox"
                  aria-expanded={roleOpen}
                >
                  <span className="login-role-trigger-left">
                    {selectedRole ? (
                      <span className="login-role-text">{selectedRole.label}</span>
                    ) : (
                      <span className="login-role-placeholder">Select your role</span>
                    )}
                  </span>
                  <LuChevronDown size={18} color="var(--n-500)" className={`login-role-chev ${roleOpen ? "rot" : ""}`} />
                </button>

                {roleOpen && (
                  <ul className="login-role-list" role="listbox">
                    {ROLE_OPTIONS.map((opt) => (
                      <li
                        key={opt.value}
                        role="option"
                        aria-selected={role === opt.value}
                        className={`login-role-item ${role === opt.value ? "is-selected" : ""}`}
                        onClick={() => {
                          setRole(opt.value);
                          setRoleOpen(false);
                          setErr("");
                        }}
                      >
                        {opt.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="login-field">
              <label className="login-label">LOGIN ID OR EMAIL</label>
              <div className="login-input-wrap">
                <LuUser size={18} color="var(--n-400)" className="login-input-icon" />
                <input
                  type="text"
                  className="login-input"
                  placeholder="name@company.com or TMP-INT-1234"
                  value={loginId}
                  onChange={(e) => {
                    setLoginId(e.target.value);
                    setErr("");
                  }}
                  autoComplete="username" aria-label="name@company.com or TMP-INT-1234" />
              </div>
            </div>

            <div className="login-field">
              <label className="login-label">PASSWORD</label>
              <div className="login-input-wrap">
                <LuLock size={18} color="var(--n-400)" className="login-input-icon" />
                <input
                  type={showPw ? "text" : "password"}
                  className="login-input"
                  placeholder="Enter your password"
                  value={pw}
                  onChange={(e) => {
                    setPw(e.target.value);
                    setErr("");
                  }}
                  autoComplete="current-password" aria-label="Enter your password" />
                <button type="button" className="login-pw-toggle" onClick={() => setShowPw(!showPw)} tabIndex={-1}>
                  {showPw ? (
                    <LuEyeOff size={18} color="var(--n-400)" />
                  ) : (
                    <LuEye size={18} color="var(--n-400)" />
                  )}
                </button>
              </div>
            </div>

            <div className="login-fp-row">
              {/* Paired with "Forgot password?" at the two ends of one row —
                  the place enterprise sign-in forms put their secondary exits.
                  Only rendered when there is somewhere to go: reaching /login
                  from a bookmark or from the 401 handler leaves no landing page
                  in the history stack, and a link that walks you out of the app
                  is worse than no link. */}
              {onBack ? (
                <button type="button" className="login-explore" onClick={onBack}>
                  Explore PSHRMS
                  <LuArrowRight size={14} strokeWidth={2.5} aria-hidden="true" />
                </button>
              ) : <span />}
              <button type="button" className="login-fp-link" onClick={() => { setAuthView("forgot-email"); setFpEmail(""); setFpErr(""); setFpInfo(""); setErr(""); }}>
                Forgot password?
              </button>
            </div>

            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? <span className="login-spinner" /> : <>Sign In <span style={{ marginLeft: 6 }}>-&gt;</span></>}
            </button>

            {/* Divider + Sign in with Google — enabled only after a role is
                chosen, because the backend needs the role to mint the token.
                Styling lives in login.css with the rest of this form: hard-coded
                hex on an inline style would have skipped the theme tokens. */}
            <div className="login-or">
              <span className="login-or-rule" />
              <span className="login-or-text">OR CONTINUE WITH</span>
              <span className="login-or-rule" />
            </div>
            <button
              type="button"
              className={`login-google-btn ${!role ? "is-locked" : ""}`}
              onClick={handleGoogle}
              disabled={!role || loading}
              title={!role ? "Select your role first" : ""}
            >
              <GoogleG /> Sign in with Google
            </button>
            {!role && (
              <div className="login-google-hint">
                Select your role above to continue with Google
              </div>
            )}
          </form>
          )}

          {/* ── Two-Step Verification: enter the 6-digit code (password login) ── */}
          {authView === "2fa" && (
            <form onSubmit={submitTwoFactor}>
              <button type="button" className="login-back" onClick={cancelTwoFactor}>← Back to login</button>
              <h2 className="login-fp-title">Two-Step Verification</h2>
              <p className="login-fp-sub">
                Open Google Authenticator or Microsoft Authenticator and enter the 6-digit code it shows for PSHRMS.
              </p>
              <p className="login-fp-note">
                Codes refresh every 30 seconds — if one is rejected, wait for the next. No access to your phone?
                Ask your system administrator to reset two-step verification.
              </p>
              <div className="login-field">
                <label className="login-label">AUTHENTICATION CODE</label>
                <input
                  className="login-input login-otp-input"
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  autoComplete="one-time-code"
                  placeholder="000000"
                  value={twoFaCode}
                  onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  maxLength={6}
                />
              </div>
              <button type="submit" className="login-btn" disabled={loading}>
                {loading ? <span className="login-spinner" /> : "Verify & Sign In"}
              </button>
            </form>
          )}

          {/* ── Step 1: Enter email ── */}
          {authView === "forgot-email" && (
            <form onSubmit={submitForgotEmail}>
              <button type="button" className="login-back" onClick={goLogin}>← Back to login</button>
              <h2 className="login-fp-title">Reset your password</h2>
              <p className="login-fp-sub">Enter your registered email and we'll send a 6-digit verification code.</p>
              {fpErr && <div className="login-error">{fpErr}</div>}
              {fpInfo && <div className="login-info">{fpInfo}</div>}
              <div className="login-field">
                <label className="login-label">EMAIL ADDRESS <span className="login-required-star">*</span></label>
                <div className="login-input-wrap">
                  <LuMail size={18} color="var(--n-400)" className="login-input-icon" />
                  <input
                    type="email"
                    className="login-input"
                    placeholder="you@company.com"
                    value={fpEmail}
                    onChange={(e) => { setFpEmail(e.target.value); setFpErr(""); }}
                    autoFocus aria-label="you@company.com" />
                </div>
              </div>
              <button className="login-btn" type="submit" disabled={fpBusy}>
                {fpBusy ? <span className="login-spinner" /> : "Send OTP"}
              </button>
              <p className="login-fp-note">Code valid for 10 minutes.</p>
            </form>
          )}

          {/* ── Step 2: Enter OTP + new password ── */}
          {authView === "forgot-otp" && (
            <form onSubmit={submitResetPassword}>
              <button type="button" className="login-back" onClick={() => setAuthView("forgot-email")}>← Back</button>
              <h2 className="login-fp-title">Enter verification code</h2>
              <p className="login-fp-sub">
                We sent a 6-digit code to <b>{maskEmail(fpEmail)}</b>. Enter it below to set a new password.
              </p>
              {fpErr && <div className="login-error">{fpErr}</div>}
              {fpInfo && <div className="login-info">{fpInfo}</div>}

              <div className="login-field">
                <label className="login-label">6-DIGIT OTP <span className="login-required-star">*</span></label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  className="login-input login-otp-input"
                  placeholder="------"
                  value={fpOtp}
                  onChange={(e) => { setFpOtp(e.target.value.replace(/\D/g, "").slice(0, 6)); setFpErr(""); }}
                  autoFocus
                  autoComplete="one-time-code" aria-label="------" />
              </div>

              <div className="login-field">
                <label className="login-label">NEW PASSWORD <span className="login-required-star">*</span></label>
                <div className="login-input-wrap">
                  <LuLock size={18} color="var(--n-400)" className="login-input-icon" />
                  <input
                    type={showNewPw ? "text" : "password"}
                    className="login-input"
                    placeholder="Min 8 chars, 1 uppercase, 1 number"
                    value={fpNewPw}
                    onChange={(e) => { setFpNewPw(e.target.value); setFpErr(""); }}
                    autoComplete="new-password"
                    spellCheck={false} aria-label="Min 8 chars, 1 uppercase, 1 number" />
                  <button type="button" className="login-pw-toggle" onClick={() => setShowNewPw((v) => !v)} tabIndex={-1}>
                    {showNewPw ? (
                      <LuEyeOff size={18} color="var(--n-400)" />
                    ) : (
                      <LuEye size={18} color="var(--n-400)" />
                    )}
                  </button>
                </div>
              </div>

              <div className="login-field">
                <label className="login-label">CONFIRM PASSWORD <span className="login-required-star">*</span></label>
                <div className="login-input-wrap">
                  <LuLock size={18} color="var(--n-400)" className="login-input-icon" />
                  <input
                    type={showNewPw ? "text" : "password"}
                    className="login-input"
                    placeholder="Re-enter the password"
                    value={fpConfirmPw}
                    onChange={(e) => { setFpConfirmPw(e.target.value); setFpErr(""); }}
                    autoComplete="new-password"
                    spellCheck={false} aria-label="Re-enter the password" />
                </div>
              </div>

              <button className="login-btn" type="submit" disabled={fpBusy}>
                {fpBusy ? <span className="login-spinner" /> : "Reset password"}
              </button>
              <div className="login-fp-resend">
                Didn't get the code?{" "}
                <button type="button" className="login-fp-link" onClick={resendOtp} disabled={resendIn > 0 || fpBusy}>
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend OTP"}
                </button>
              </div>
            </form>
          )}

          {/* ── Step 3: Success ── */}
          {authView === "forgot-success" && (
            <div className="login-fp-success">
              <div className="login-fp-success-icon" aria-hidden="true">
                <LuCheck size={48} color="var(--success-500)" strokeWidth={2.5} />
              </div>
              <h2 className="login-fp-title">Password reset successfully</h2>
              <p className="login-fp-sub">You can now sign in with your new password.</p>
              <button className="login-btn" type="button" onClick={goLogin}>
                Go to login
              </button>
            </div>
          )}

          <div className="login-footer">
            <span>Ponsuvai Foods Private Limited</span>
            <span>PSHRMS v1.0 | Human Resource Management System</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
